import Head from 'next/head';
import Script from 'next/script';
import { ChangeEvent, Fragment, ReactNode, useState, useCallback, useMemo } from 'react';

import {
  Button,
  Text,
  VStack,
  Flex,
  type FlexProps,
  rem,
  IconButton,
  Tooltip,
  Show,
  Hide,
} from '@vimeo/bokeh';
import { VideosStack, CircleExclamation, DownloadImport } from '@vimeo/bokeh-icons';
import { Video } from '@vimeo/gctl-api/models/components/schemas/video/Video';
import { translate } from '@vimeo/i18n-runtime';
import { useOrionSettings } from '@vimeo/orion';
import {
  AutoplayToggle,
  Playlist,
  PlaylistHeader,
  PlaylistGrid,
  VideoCard,
  SPIcon,
  VideoCardSkeleton,
  Skeleton,
} from '@vimeo/playlist';

import { withResponsiveValue } from '../../hocs/withResponsiveValue';
import { ShowcasePlaylistHookProps, useShowcasePlaylist } from '../../hooks/use-showcase-playlist';
import { getClipIdFromUri, getClipThumbnails } from '../../utils/helper';
import { Clip, GridSize, ResponsiveValue } from '../../utils/types';
import { VideoCardPlayer } from '../VideoCardPlayer';

interface ShowcasePlaylistProps
  extends FlexProps,
    Omit<ShowcasePlaylistHookProps, 'getResponsiveValue'> {
  clips?: Array<Clip>;
  total?: number;
  activeClipId?: string | null;
  isAutoplayDefaultChecked?: boolean;
  allowDownloads: boolean | undefined;
  onAutoplayChange?: (checked: boolean) => void;
  onAutoplayClick?: (event: ChangeEvent<HTMLInputElement>) => void;
  onVideoCardClick: (
    link: string,
    clipId: string,
    videoPrivacy: string,
    userUri: string,
    live?: Clip['live'],
  ) => void;
  onDownloadClick?: (clipId: string) => void;
  showProfileImage?: boolean;
  showProfileName?: boolean;
  showVideoTitle?: boolean;
  showVideoCount?: boolean;
  isNewCustomizeControls: boolean;
}

const isStaffPick = (badge: string) =>
  [
    'staffpick',
    'staffpick-premiere',
    'staffpick-best-of-the-year',
    'staffpick-best-of-the-month',
  ].includes(badge);

const isArchivedOrNonLive = (clip: Video) =>
  (clip.live && clip.live?.status === 'done') || !clip.live;

const ShowcasePlaylistComponent = ({
  clips,
  gridSize,
  activeClipId,
  total,
  showVideoCount = true,
  isAutoplayDefaultChecked,
  isShowcasePreview,
  onAutoplayChange,
  onAutoplayClick,
  onVideoCardClick,
  allowDownloads,
  onDownloadClick,
  isMobilePreview,
  getResponsiveValue,
  showProfileImage,
  showProfileName,
  showVideoTitle,
  showVideoPlayerCards,
  isNewCustomizeControls,
}: ShowcasePlaylistProps & ResponsiveValue) => {
  const { settings } = useOrionSettings();
  const shouldShowAutoplayVideoPreview = Boolean(settings.showcase_autoplay_video_preview);
  const shouldShowAnimatedThumbnail = shouldShowAutoplayVideoPreview && !showVideoPlayerCards;
  const sdkScriptUrl = window?.playerAssetUrls?.player_api_js ?? null;
  const [isScriptLoaded, setScriptLoaded] = useState(
    typeof window !== 'undefined' && Boolean(window?.Vimeo?.Player),
  );
  const {
    direction,
    effectiveGridSize,
    gridGap,
    gridTemplateColumns,
    handleVideoCardClick,
    numColumns,
  } = useShowcasePlaylist({
    getResponsiveValue,
    gridSize,
    isMobilePreview,
    isShowcasePreview,
    onVideoCardClick,
    showVideoPlayerCards,
    isNewCustomizeControls,
  });

  const handleScriptReady = useCallback(() => {
    setScriptLoaded(Boolean(window?.Vimeo?.Player));
  }, []);

  const preconnectOrigins = useMemo(() => {
    if (!shouldShowAnimatedThumbnail) return [];

    const origins = new Set<string>();
    const addOrigin = (url?: string | null) => {
      if (!url) return;
      origins.add(new URL(url, window.location.origin).origin);
    };

    addOrigin(sdkScriptUrl);
    clips?.forEach((clip) => addOrigin(clip.playerEmbedUrl));

    return Array.from(origins);
  }, [clips, sdkScriptUrl, shouldShowAnimatedThumbnail]);

  if (!clips || clips.length === 0) return null;

  return (
    <>
      {preconnectOrigins.length > 0 && (
        <Head>
          {preconnectOrigins.map((origin) => (
            <link key={`preconnect-${origin}`} rel="preconnect" href={origin} crossOrigin="" />
          ))}
        </Head>
      )}
      {sdkScriptUrl && (
        <Script src={sdkScriptUrl} onReady={handleScriptReady} onLoad={handleScriptReady} />
      )}
      <Playlist>
        <PlaylistHeader
          header={
            showVideoCount
              ? {
                  text: translate({
                    singular: '{total} video',
                    plural: '{total} videos',
                    count: total,
                    replacements: {
                      total: total,
                    },
                  }),
                  as: !isShowcasePreview ? 'h2' : undefined,
                }
              : undefined
          }
          px={getResponsiveValue({
            base: 'md',
            lg: 'lg',
          })}
        >
          <AutoplayToggle
            id="showcase_autoplay"
            ttl={7}
            defaultChecked={isAutoplayDefaultChecked}
            onCheckedChange={onAutoplayChange}
            onChange={onAutoplayClick}
          />
        </PlaylistHeader>

        <PlaylistGrid
          numColumns={numColumns}
          px={getResponsiveValue({ base: 'sm', sm: 'md', lg: 'lg' })}
          {...(!isNewCustomizeControls && { gridTemplateColumns })}
          gap={gridGap}
        >
          {clips.map((clip) => {
            const { thumbnail, thumbnail2x } = getClipThumbnails(clip, effectiveGridSize);

            return (
              <VideoCard
                {...((showVideoPlayerCards || shouldShowAnimatedThumbnail) && {
                  thumbnail: (
                    <VideoCardPlayer
                      clip={clip}
                      clipId={clip.clipId || getClipIdFromUri(clip.uri)}
                      embedPlayerConfigUrl={clip.embedPlayerConfigUrl}
                      name={clip.name}
                      playerEmbedUrl={clip.playerEmbedUrl}
                      thumbnailSrc={shouldShowAnimatedThumbnail ? thumbnail : undefined}
                      thumbnailSrcSet={shouldShowAnimatedThumbnail ? thumbnail2x : undefined}
                      playOnHover={shouldShowAnimatedThumbnail}
                      isScriptLoaded={isScriptLoaded}
                    />
                  ),
                })}
                key={clip.uri}
                alt={clip.name}
                {...(showVideoTitle && { name: clip.name })}
                showUserAvatar={showProfileImage}
                showUserName={showProfileName}
                src={thumbnail} // Event's live clips that never streamed has no thumbnail returned here
                srcSet={thumbnail2x}
                loading="lazy"
                duration={clip.duration}
                user={clip.user}
                role="link"
                as="a"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore VideoCard uses CardProps, but we need to pass href for using as `a` element
                href={clip.link}
                data-href={clip.link}
                direction={direction}
                borderRadius="lg"
                px="sm"
                py="sm"
                onClick={(e) => handleVideoCardClick(e, clip)}
                onKeyUp={(e) => {
                  e.key === 'Enter' &&
                    onVideoCardClick(
                      isShowcasePreview ? clip.uri : clip.link,
                      clip.clipId || '',
                      clip.privacy.view,
                      clip.user.uri,
                      clip.live,
                    );
                }}
                cardDetailsStylingProps={{
                  alignItems:
                    showProfileImage && (!showProfileName || !showVideoTitle)
                      ? 'center'
                      : 'flex-start',
                }}
                {...(isStaffPick(clip?.badge?.type || '') && { topLeftDecoration: <SPIcon /> })}
                {...(activeClipId && clip?.clipId === activeClipId && { trackStatus: 'playing' })}
                {...(clip?.live?.status === 'streaming' && { trackStatus: 'live' })}
              >
                {allowDownloads && clip.privacy.download && isArchivedOrNonLive(clip) ? (
                  <Tooltip label={translate('Download')}>
                    <IconButton
                      aria-label="Download button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        clip.clipId && onDownloadClick?.(clip.clipId);
                      }}
                      variant="minimal"
                      size="sm"
                      icon={<DownloadImport />}
                      opacity={getResponsiveValue({ base: 1, md: 0 })}
                      _groupHover={{ opacity: 1 }}
                      _groupFocusWithin={{ opacity: 1 }}
                    >
                      {translate('Download')}
                    </IconButton>
                  </Tooltip>
                ) : null}
              </VideoCard>
            );
          })}
        </PlaylistGrid>
      </Playlist>
    </>
  );
};

interface ShowcasePlaylistSkeletonProps {
  total?: number;
  gridSize?: GridSize;
  isAutoplayDefaultChecked?: boolean;
  onAutoplayChange?: (checked: boolean) => void;
  length?: number;
  isMobilePreview?: boolean;
  showVideoPlayerCards?: boolean;
  isNewCustomizeControls: boolean;
}

const ShowcasePlaylistSkeletonComponent = ({
  total,
  gridSize,
  isAutoplayDefaultChecked,
  onAutoplayChange,
  length,
  getResponsiveValue,
  showVideoPlayerCards,
  isNewCustomizeControls,
  ...rest
}: FlexProps & ShowcasePlaylistSkeletonProps & ResponsiveValue) => {
  const { gridGap, gridTemplateColumns, numColumns } = useShowcasePlaylist({
    gridSize,
    getResponsiveValue,
    showVideoPlayerCards,
    isNewCustomizeControls,
  });

  return (
    <Flex maxW={rem(1536)} w="100%" {...rest}>
      <VStack
        borderRadius="md"
        width="100%"
        align="flex-start"
        gap="lg"
        p={getResponsiveValue({
          base: 'md',
          lg: 'lg',
        })}
      >
        {total ? (
          <PlaylistHeader
            width="100%"
            header={{
              text: translate({
                singular: '{total} video',
                plural: '{total} videos',
                count: total,
                replacements: {
                  total: total,
                },
              }),
            }}
          >
            <AutoplayToggle
              id="showcase_autoplay"
              ttl={7}
              defaultChecked={isAutoplayDefaultChecked}
              onCheckedChange={onAutoplayChange}
            />
          </PlaylistHeader>
        ) : (
          <Skeleton w="150px" h="1.875rem" />
        )}

        <PlaylistGrid
          numColumns={numColumns}
          {...(!isNewCustomizeControls && { gridTemplateColumns })}
          w="100%"
          p="0"
          gap={gridGap}
        >
          {Array.from({ length: length || 8 }).map((_, index) => (
            <Fragment key={`playlist-loading-skeleton-${index}`}>
              {isNewCustomizeControls ? (
                <VideoCardSkeleton numOfLines={2} />
              ) : (
                <>
                  <Hide above="sm">
                    <Skeleton h={rem(92)} my="sm" borderRadius="button" />
                  </Hide>
                  <Show above="sm">
                    <VideoCardSkeleton numOfLines={3} />
                  </Show>
                </>
              )}
            </Fragment>
          ))}
        </PlaylistGrid>
      </VStack>
    </Flex>
  );
};

export const ShowcasePlaylistErrorView = ({ onRetry }: { onRetry: () => void }) => (
  <VStack alignItems="center" justifyContent="center" h={{ base: rem(412), md: rem(538) }} gap="4">
    <CircleExclamation boxSize={{ base: 'sm', md: 'lg' }} />
    <Text variant={{ base: 'heading-sm', md: 'heading-md' }}>
      {translate('Couldn’t load videos')}
    </Text>
    <Button variant="primary" size={{ base: 'sm', md: 'md' }} onClick={onRetry}>
      {translate('Try again')}
    </Button>
  </VStack>
);

interface ShowcasePlaylistEmptyViewProps {
  isMobilePreview?: boolean;
  addVideoBtn?: ReactNode;
}

const ShowcasePlaylistEmptyViewComponent = ({
  getResponsiveValue,
  addVideoBtn,
}: ShowcasePlaylistEmptyViewProps & ResponsiveValue) => (
  <VStack alignItems="center" justifyContent="center" color="text-primary" py="lg" h="100%" gap="4">
    <VideosStack boxSize={getResponsiveValue({ base: 'sm', md: 'lg' })} />
    <Text variant={getResponsiveValue({ base: 'heading-sm', md: 'heading-md' })}>
      {translate('This showcase is currently empty')}
    </Text>
    {addVideoBtn}
  </VStack>
);

export const ShowcasePlaylist =
  withResponsiveValue<ShowcasePlaylistProps>(ShowcasePlaylistComponent);

export const ShowcasePlaylistSkeleton = withResponsiveValue<ShowcasePlaylistSkeletonProps>(
  ShowcasePlaylistSkeletonComponent,
);

export const ShowcasePlaylistEmptyView = withResponsiveValue<ShowcasePlaylistEmptyViewProps>(
  ShowcasePlaylistEmptyViewComponent,
);
